import React, { useMemo, useState, useContext } from "react";
import {
  IonCard,
  IonCardContent,
  IonRow,
  IonCol,
  IonAvatar,
  IonLabel,
  IonButton,
  IonIcon,
  IonChip,
  IonModal,
  IonContent,
} from "@ionic/react";
import {
  checkboxOutline,
  chatboxOutline,
  shareSocialOutline,
  closeOutline,
} from "ionicons/icons";
import { ProfileContext } from "../context/ProfileContext";
import TimeAgo from "javascript-time-ago";
import en from "javascript-time-ago/locale/en.json";

type Announcement = {
  id: string | number;
  avatar: string;
  name: string;
  groupName?: string;
  createdAt: number; // keep as string for now
  content: string;
  image?: string;
};

const AnnouncementFeed: React.FC = () => {
  const { profile } = useContext(ProfileContext);
  TimeAgo.addDefaultLocale(en);
  const timeAgo = new TimeAgo("en-US");

  // Local data for now (replace with Firestore later)
  const AnnouncementsData: Announcement[] = useMemo(
    () => [
      {
        id: 1,
        avatar: profile.ProfilePicURL,
        name: "Dave Wigley",
        groupName: "Avalon Job",
        createdAt: new Date("2024-12-04T11:23:00").getTime(),
        content: "Don't forget to send W9's. Send them to dwigley@oldemail.com",
        image: "",
      },
      {
        id: 2,
        avatar: profile.ProfilePicURL,
        name: "Chris Chribs",
        groupName: "Cadre-Daytona2",
        createdAt: Date.now() - 1000 * 60 * 12,
        content:
          "Someone left this on the job site. Just a reminder, do not be that guy...",
        image: profile.ProfilePicURL,
      },
    ],
    [profile.ProfilePicURL]
  );

  const [previewImage, setPreviewImage] = useState<string>("");
  const [acknowledgedIds, setAcknowledgedIds] = useState<
    Record<string | number, boolean>
  >({});

  const handleAcknowledge = (id: string | number) => {
    setAcknowledgedIds((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleReply = (_id: string | number) => {
    // Placeholder for later:
    // - open a DM thread
    // - or open an "announcement replies" view
    // console.log("Reply to announcement:", id);
  };

  const handleShare = (_id: string | number) => {
    // Placeholder for later:
    // - Capacitor Share plugin
    // - copy link
    // console.log("Share announcement:", id);
  };

  return (
    <>
      <div style={{ paddingBottom: "12px" }}>
        {AnnouncementsData.map((a) => {
          const isAck = acknowledgedIds[a.id] === true;

          return (
            <IonCard
              key={a.id}
              style={{
                marginLeft: "12px",
                marginRight: "12px",
                marginTop: "12px",
                borderRadius: "14px",
              }}
            >
              {/* Header Row */}
              <IonRow
                style={{ padding: "12px 12px 0px 12px" }}
                className="ion-align-items-center"
              >
                <IonCol
                  size="auto"
                  style={{ paddingRight: "10px" }}
                >
                  <IonAvatar
                    style={{ width: "36px", height: "36px" }}
                  >
                    <img src={a.avatar} alt={`${a.name}'s avatar`} />
                  </IonAvatar>
                </IonCol>

                <IonCol
                  style={{ paddingLeft: 0 }}
                >
                  <IonLabel
                  >
                    <h2 style={{ margin: 0, fontSize: "1rem" }}>{a.name}</h2>

                    <div
                      style={{ display: "flex", gap: "8px", marginTop: "2px" }}
                    >
                      {a.groupName && (
                        <IonChip
                          style={{ height: "22px" }}
                        >
                          <IonLabel
                            style={{ fontSize: "0.8rem" }}
                          >
                            {a.groupName}
                          </IonLabel>
                        </IonChip>
                      )}

                      <span
                        style={{
                          fontSize: "0.8rem",
                          opacity: 0.7,
                          alignSelf: "center",
                        }}
                      >
                          {timeAgo.format(new Date(a.createdAt))}
                      </span>
                    </div>
                  </IonLabel>
                </IonCol>
              </IonRow>

              {/* Content */}
              <IonCardContent
                style={{ paddingTop: "10px" }}
              >
                <p
                  style={{
                    marginTop: 0,
                    marginBottom: a.image ? "10px" : "0px",
                  }}
                >
                  {a.content}
                </p>

                {a.image && a.image.trim() !== "" && (
                  <img
                    src={a.image}
                    alt="Post"
                    onClick={() => setPreviewImage(a.image!)}
                    style={{
                      width: "100%",
                      borderRadius: "12px",
                      cursor: "pointer",
                    }}
                  />
                )}
              </IonCardContent>

              {/* Footer Actions */}
              <IonRow
                style={{
                  padding: "0px 8px 10px 8px",
                  borderTop: "1px solid rgba(0,0,0,0.06)",
                }}
                className="ion-justify-content-around"
              >
                <IonCol
                  className="ion-text-center"
                >
                  <IonButton
                    size="small"
                    fill="clear"
                    color={isAck ? "success" : "medium"}
                    onClick={() => handleAcknowledge(a.id)}
                  >
                    <IonIcon
                      icon={checkboxOutline}
                      slot="start"
                    />
                    {isAck ? "Acknowledged" : "Acknowledge"}
                  </IonButton>
                </IonCol>

                <IonCol
                  className="ion-text-center"
                >
                  <IonButton
                    size="small"
                    fill="clear"
                    color="medium"
                    onClick={() => handleReply(a.id)}
                  >
                    <IonIcon
                      icon={chatboxOutline}
                      slot="start"
                    />
                    Reply
                  </IonButton>
                </IonCol>

                <IonCol
                  className="ion-text-center"
                >
                  <IonButton
                    size="small"
                    fill="clear"
                    color="medium"
                    onClick={() => handleShare(a.id)}
                  >
                    <IonIcon
                      icon={shareSocialOutline}
                      slot="start"
                    />
                    Share
                  </IonButton>
                </IonCol>
              </IonRow>
            </IonCard>
          );
        })}
      </div>

      {/* Image preview modal */}
      <IonModal
        isOpen={previewImage !== ""}
        onDidDismiss={() => setPreviewImage("")}
      >
        <IonContent
          fullscreen
        >
          <div style={{ padding: "12px" }}>
            <IonButton
              fill="clear"
              onClick={() => setPreviewImage("")}
            >
              <IonIcon
                icon={closeOutline}
                slot="icon-only"
              />
            </IonButton>

            {previewImage && (
              <img
                src={previewImage}
                alt="Preview"
                style={{
                  width: "100%",
                  borderRadius: "14px",
                  marginTop: "10px",
                }}
              />
            )}
          </div>
        </IonContent>
      </IonModal>
    </>
  );
};

export default AnnouncementFeed;
